import React, { useState, useEffect } from 'react';
import {
  EnvelopeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  UserGroupIcon,
  CalendarDaysIcon,
  FunnelIcon,
  ArrowPathIcon,
  PlayIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import moment from 'moment';
import { toast } from 'react-toastify';

function EmailStats() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    type: '',
    periodStart: '',
    periodEnd: '',
  });
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [triggeringWeekly, setTriggeringWeekly] = useState(false);
  const [triggeringMonthly, setTriggeringMonthly] = useState(false);

  useEffect(() => {
    loadEmailStats();
  }, [filters]);

  const loadEmailStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (filters.type) params.type = filters.type;
      if (filters.periodStart) params.periodStart = filters.periodStart;
      if (filters.periodEnd) params.periodEnd = filters.periodEnd;
      
      const response = await api.get('/team-summary/email-stats', { params });
      
      // Handle empty response or error response
      if (!response.data || response.data.success === false) {
        setError(response.data?.error || 'Failed to load email stats');
        setStats({
          stats: [],
          groupedStats: [],
          total: 0,
          uniquePeriods: [],
          message: response.data?.message
        });
      } else {
        setStats(response.data);
        if (response.data.message) {
          toast.info(response.data.message);
        }
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to load email stats';
      setError(errorMessage);
      setStats({
        stats: [],
        groupedStats: [],
        total: 0,
        uniquePeriods: [],
        message: err.response?.data?.message
      });
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      type: '',
      periodStart: '',
      periodEnd: '',
    });
    setSelectedPeriod(null);
  };

  const handlePeriodSelect = (period) => {
    if (selectedPeriod && selectedPeriod.emailType === period.emailType && 
        selectedPeriod.periodStart === period.periodStart) {
      setSelectedPeriod(null);
      clearFilters();
    } else {
      setSelectedPeriod(period);
      setFilters({
        type: period.emailType,
        periodStart: moment(period.periodStart).format('YYYY-MM-DD'),
        periodEnd: moment(period.periodEnd).format('YYYY-MM-DD'),
      });
    }
  };

  const triggerWeeklyEmails = async () => {
    if (!window.confirm('Are you sure you want to trigger weekly team summary emails? This will send emails to all managers.')) {
      return;
    }
    
    try {
      setTriggeringWeekly(true);
      const response = await api.post('/team-summary/weekly/trigger');
      
      if (response.data.success) {
        toast.success(response.data.message || 'Weekly emails triggered successfully!');
        // Reload stats after a short delay
        setTimeout(() => {
          loadEmailStats();
        }, 2000);
      } else {
        toast.error(response.data.error || 'Failed to trigger weekly emails');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to trigger weekly emails';
      toast.error(errorMessage);
    } finally {
      setTriggeringWeekly(false);
    }
  };

  const triggerMonthlyEmails = async () => {
    if (!window.confirm('Are you sure you want to trigger monthly team summary emails? This will send emails to all managers.')) {
      return;
    }
    
    try {
      setTriggeringMonthly(true);
      const response = await api.post('/team-summary/monthly/trigger');
      
      if (response.data.success) {
        toast.success(response.data.message || 'Monthly emails triggered successfully!');
        // Reload stats after a short delay
        setTimeout(() => {
          loadEmailStats();
        }, 2000);
      } else {
        toast.error(response.data.error || 'Failed to trigger monthly emails');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to trigger monthly emails';
      toast.error(errorMessage);
    } finally {
      setTriggeringMonthly(false);
    }
  };

  const getSummaryStats = () => {
    if (!stats || !stats.groupedStats) return null;
    
    const allStats = stats.groupedStats;
    const totalEmails = allStats.reduce((sum, g) => sum + g.totalEmails, 0);
    const successfulEmails = allStats.reduce((sum, g) => sum + g.successfulEmails, 0);
    const failedEmails = allStats.reduce((sum, g) => sum + g.failedEmails, 0);
    const totalTeamMembers = allStats.reduce((sum, g) => sum + g.totalTeamMembers, 0);
    const totalWorkingHours = allStats.reduce((sum, g) => sum + g.totalWorkingHours, 0);
    const totalExpectedHours = allStats.reduce((sum, g) => sum + g.totalExpectedHours, 0);
    const totalLeaves = allStats.reduce((sum, g) => sum + g.totalLeaves, 0);
    const totalCheckoutRequests = allStats.reduce((sum, g) => sum + g.totalCheckoutRequests, 0);

    return {
      totalEmails,
      successfulEmails,
      failedEmails,
      totalTeamMembers,
      totalWorkingHours,
      totalExpectedHours,
      totalLeaves,
      totalCheckoutRequests,
      successRate: totalEmails > 0 ? ((successfulEmails / totalEmails) * 100).toFixed(1) : 0,
    };
  };

  const summaryStats = getSummaryStats();
  const displayStats = selectedPeriod 
    ? stats?.groupedStats?.find(g => 
        g.emailType === selectedPeriod.emailType &&
        moment(g.periodStart).format('YYYY-MM-DD') === selectedPeriod.periodStart &&
        moment(g.periodEnd).format('YYYY-MM-DD') === selectedPeriod.periodEnd
      )?.stats || stats?.stats || []
    : stats?.stats || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <ArrowPathIcon className="h-12 w-12 text-indigo-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading email stats...</p>
        </div>
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <XCircleIcon className="h-5 w-5 text-red-400 mr-2" />
            <div>
              <p className="text-red-800 font-semibold">{error}</p>
              {stats?.message && (
                <p className="text-red-600 text-sm mt-2">{stats.message}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Show message if stats exist but are empty
  if (stats?.message && (!stats.stats || stats.stats.length === 0)) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-fuchsia-600 rounded-3xl shadow-xl p-6 sm:p-7 lg:p-8 text-white">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
              <div className="space-y-2">
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight drop-shadow">
                  Team Summary Email Stats
                </h1>
                <p className="text-white/85 text-sm sm:text-base max-w-xl">
                  Monitor weekly and monthly team summary emails sent to managers
                </p>
              </div>
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="flex gap-2">
              <button
                onClick={triggerWeeklyEmails}
                disabled={triggeringWeekly}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-white font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {triggeringWeekly ? (
                  <>
                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <PlayIcon className="h-4 w-4" />
                    Trigger Weekly
                  </>
                )}
              </button>
              <button
                onClick={triggerMonthlyEmails}
                disabled={triggeringMonthly}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-white font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {triggeringMonthly ? (
                  <>
                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <PlayIcon className="h-4 w-4" />
                    Trigger Monthly
                  </>
                )}
              </button>
            </div>
            <div className="h-14 w-14 sm:h-16 sm:w-16 lg:h-20 lg:w-20 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center border border-white/40 shadow-2xl ring-4 ring-white/10">
              <EnvelopeIcon className="h-8 w-8 sm:h-10 sm:w-10 lg:h-12 lg:w-12 text-white" />
            </div>
          </div>
            </div>
          </div>
          
          {/* Message */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <EnvelopeIcon className="h-6 w-6 text-blue-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-blue-800">No Email Stats Yet</h3>
                <p className="text-blue-600 mt-2">{stats.message}</p>
                <p className="text-blue-500 text-sm mt-3">
                  To create the table now, set <code className="bg-blue-100 px-2 py-1 rounded">SEQUELIZE_ALTER=true</code> in your environment and restart the server.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-fuchsia-600 rounded-3xl shadow-xl p-6 sm:p-7 lg:p-8 text-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
          <div className="space-y-2">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight drop-shadow">
              Team Summary Email Stats
            </h1>
            <p className="text-white/85 text-sm sm:text-base max-w-xl">
              Monitor weekly and monthly team summary emails sent to managers
            </p>
          </div>
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="flex gap-2">
              <button
                onClick={triggerWeeklyEmails}
                disabled={triggeringWeekly}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-white font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {triggeringWeekly ? (
                  <>
                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <PlayIcon className="h-4 w-4" />
                    Trigger Weekly
                  </>
                )}
              </button>
              <button
                onClick={triggerMonthlyEmails}
                disabled={triggeringMonthly}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-white font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {triggeringMonthly ? (
                  <>
                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <PlayIcon className="h-4 w-4" />
                    Trigger Monthly
                  </>
                )}
              </button>
            </div>
            <div className="h-14 w-14 sm:h-16 sm:w-16 lg:h-20 lg:w-20 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center border border-white/40 shadow-2xl ring-4 ring-white/10">
              <EnvelopeIcon className="h-8 w-8 sm:h-10 sm:w-10 lg:h-12 lg:w-12 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {summaryStats && (
        <div className="grid grid-cols-1 gap-4 sm:gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Emails</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{summaryStats.totalEmails}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {summaryStats.successfulEmails} successful, {summaryStats.failedEmails} failed
                </p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-indigo-100 flex items-center justify-center">
                <EnvelopeIcon className="h-6 w-6 text-indigo-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Success Rate</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{summaryStats.successRate}%</p>
                <p className="text-xs text-gray-500 mt-1">
                  {summaryStats.successfulEmails} / {summaryStats.totalEmails} emails
                </p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-green-100 flex items-center justify-center">
                <CheckCircleIcon className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Team Members</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{summaryStats.totalTeamMembers}</p>
                <p className="text-xs text-gray-500 mt-1">Across all managers</p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-purple-100 flex items-center justify-center">
                <UserGroupIcon className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Working Hours</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  {summaryStats.totalWorkingHours.toFixed(1)}h
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Expected: {summaryStats.totalExpectedHours.toFixed(1)}h
                </p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
                <ClockIcon className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Type
            </label>
            <select
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
              className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            >
              <option value="">All Types</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Period Start
            </label>
            <input
              type="date"
              value={filters.periodStart}
              onChange={(e) => handleFilterChange('periodStart', e.target.value)}
              className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Period End
            </label>
            <input
              type="date"
              value={filters.periodEnd}
              onChange={(e) => handleFilterChange('periodEnd', e.target.value)}
              className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>
          <div>
            <button
              onClick={clearFilters}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Unique Periods */}
      {stats?.uniquePeriods && stats.uniquePeriods.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <CalendarDaysIcon className="h-5 w-5 mr-2 text-indigo-600" />
            Available Periods
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {stats.uniquePeriods.map((period, index) => {
              const isSelected = selectedPeriod && 
                selectedPeriod.emailType === period.emailType &&
                moment(selectedPeriod.periodStart).format('YYYY-MM-DD') === moment(period.periodStart).format('YYYY-MM-DD');
              
              return (
                <button
                  key={index}
                  onClick={() => handlePeriodSelect(period)}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    isSelected
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-gray-900 capitalize">{period.emailType}</p>
                      <p className="text-sm text-gray-600 mt-1">
                        {moment(period.periodStart).format('MMM DD, YYYY')} - {moment(period.periodEnd).format('MMM DD, YYYY')}
                      </p>
                    </div>
                    {isSelected && (
                      <CheckCircleIcon className="h-5 w-5 text-indigo-600" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Email Stats Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Email Details {selectedPeriod && `(${selectedPeriod.emailType} - ${moment(selectedPeriod.periodStart).format('MMM DD')} to ${moment(selectedPeriod.periodEnd).format('MMM DD')})`}
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Manager
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Team Members
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Working Hours
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Expected Hours
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Leaves
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Checkout Requests
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sent At
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {displayStats.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-6 py-12 text-center text-gray-500">
                    No email stats found
                  </td>
                </tr>
              ) : (
                displayStats.map((stat, index) => (
                  <tr key={stat.id || index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {stat.managerName || stat.manager?.firstName + ' ' + stat.manager?.lastName}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600">
                        {stat.managerEmail || stat.manager?.officialEmail}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="text-sm text-gray-900">{stat.teamMembers || 0}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="text-sm text-gray-900">
                        {parseFloat(stat.workingHours || 0).toFixed(1)}h
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="text-sm text-gray-900">
                        {parseFloat(stat.expectedHours || 0).toFixed(1)}h
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="text-sm text-gray-900">{stat.leaves || 0}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="text-sm text-gray-900">{stat.checkoutRequests || 0}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {stat.success ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircleIcon className="h-4 w-4 mr-1" />
                          Sent
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <XCircleIcon className="h-4 w-4 mr-1" />
                          Failed
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600">
                        {stat.sentAt ? moment(stat.sentAt).format('MMM DD, YYYY HH:mm') : '-'}
                      </div>
                      {stat.error && (
                        <div className="text-xs text-red-600 mt-1" title={stat.error}>
                          {stat.error.length > 50 ? stat.error.substring(0, 50) + '...' : stat.error}
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {stats && stats.total > displayStats.length && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <p className="text-sm text-gray-600">
              Showing {displayStats.length} of {stats.total} emails
            </p>
          </div>
        )}
      </div>
    </div>
    </div>
  );
}

export default EmailStats;